import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import {
  ArrowLeft,
  Wrench,
  Laptop,
  FileText,
  CheckCircle,
  Circle,
  Clock,
  AlertTriangle,
  Download,
  Loader2,
} from "lucide-react";

const statusLabels = {
  appareil_depose: "Appareil déposé",
  en_attente_diagnostic: "En attente diagnostic",
  diagnostic_en_cours: "Diagnostic en cours",
  devis_en_cours: "Devis en cours",
  devis_envoye: "Devis envoyé",
  devis_accepte: "Devis accepté",
  devis_refuse: "Devis refusé",
  en_attente_pieces: "En attente de pièces",
  reparation_en_cours: "Réparation en cours",
  reparation_terminee: "Réparation terminée",
  pret_a_restituer: "Prêt à restituer",
  restitue: "Restitué",
  annule: "Annulé",
};

const timelineSteps = [
  { key: "appareil_depose", label: "Dépôt", icon: Laptop },
  { key: "diagnostic", label: "Diagnostic", icon: Wrench, matches: ["en_attente_diagnostic", "diagnostic_en_cours"] },
  { key: "devis", label: "Devis", icon: FileText, matches: ["devis_en_cours", "devis_envoye", "devis_accepte", "devis_refuse"] },
  { key: "reparation", label: "Réparation", icon: Wrench, matches: ["en_attente_pieces", "reparation_en_cours", "reparation_terminee"] },
  { key: "restitution", label: "Restitution", icon: CheckCircle, matches: ["pret_a_restituer", "restitue"] },
];

function getStepStatus(step, currentStatut) {
  const allStatuses = [
    "appareil_depose",
    "en_attente_diagnostic", "diagnostic_en_cours",
    "devis_en_cours", "devis_envoye", "devis_accepte", "devis_refuse",
    "en_attente_pieces", "reparation_en_cours", "reparation_terminee",
    "pret_a_restituer", "restitue",
  ];
  const currentIdx = allStatuses.indexOf(currentStatut);
  const stepStatuses = step.matches || [step.key];
  const stepIndices = stepStatuses.map((s) => allStatuses.indexOf(s)).filter((i) => i >= 0);
  const maxStepIdx = Math.max(...stepIndices);
  const minStepIdx = Math.min(...stepIndices);

  if (currentIdx > maxStepIdx) return "completed";
  if (currentIdx >= minStepIdx && currentIdx <= maxStepIdx) return "current";
  return "pending";
}

const ClientPortalSAVDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [downloadingPdf, setDownloadingPdf] = useState(null);

  const downloadPdf = async (url, filename) => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    setDownloadingPdf(url);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Erreur téléchargement");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Erreur lors du téléchargement du PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  useEffect(() => {
    const fetchCase = async () => {
      try {
        const res = await API.get(`/portal/sav-cases/${id}`);
        setCaseData(res.data);
      } catch (error) {
        toast.error("Erreur lors du chargement du dossier");
        navigate("/portal/sav");
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [id, navigate]);

  const handleQuoteResponse = async (quoteId, action) => {
    try {
      await API.put(`/portal/quotes/${quoteId}/respond`, { action });
      toast.success(action === "accepte" ? "Devis accepté" : "Devis refusé");
      const res = await API.get(`/portal/sav-cases/${id}`);
      setCaseData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!caseData) return null;

  return (
    <div className="space-y-6" data-testid="client-portal-sav-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/portal/sav")} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{caseData.numero}</h1>
          <p className="text-muted-foreground">
            {caseData.device?.marque} {caseData.device?.modele}
          </p>
        </div>
        <button
          onClick={() => downloadPdf(`/api/portal/pdf/bon-depot/${id}`, `BonDepot_${caseData.numero}.pdf`)}
          disabled={downloadingPdf === `/api/portal/pdf/bon-depot/${id}`}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
          data-testid="download-bon-depot-btn"
        >
          {downloadingPdf === `/api/portal/pdf/bon-depot/${id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Bon de dépôt
        </button>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Avancement de la réparation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between relative">
            {/* Progress bar */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-border mx-12" />
            {timelineSteps.map((step, idx) => {
              const status = getStepStatus(step, caseData.statut);
              const Icon = step.icon;
              return (
                <div key={step.key} className="flex flex-col items-center relative z-10 flex-1">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${
                      status === "completed"
                        ? "bg-green-500 border-green-500 text-white"
                        : status === "current"
                        ? "bg-primary border-primary text-primary-foreground animate-pulse"
                        : "bg-background border-border text-muted-foreground"
                    }`}
                    data-testid={`timeline-step-${step.key}`}
                  >
                    {status === "completed" ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : status === "current" ? (
                      <Clock className="h-5 w-5" />
                    ) : (
                      <Circle className="h-5 w-5" />
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 font-medium text-center ${
                      status === "current" ? "text-primary" : status === "completed" ? "text-green-600" : "text-muted-foreground"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <Badge className="text-sm px-4 py-1">
              {statusLabels[caseData.statut] || caseData.statut}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Device Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Laptop className="h-5 w-5" />
            Appareil
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Marque</p>
              <p className="font-medium">{caseData.device?.marque || "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Modèle</p>
              <p className="font-medium">{caseData.device?.modele || "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Catégorie</p>
              <p className="font-medium capitalize">{caseData.device?.categorie?.replace(/_/g, " ") || "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Type</p>
              <p className="font-medium capitalize">{caseData.type_prise_en_charge === "garantie" ? "Sous garantie" : "Hors garantie"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotes */}
      {caseData.quotes && caseData.quotes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Devis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {caseData.quotes.map((quote) => (
              <div key={quote.id} className="border rounded-lg p-4" data-testid={`quote-${quote.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold">{quote.numero}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(quote.created_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                  <Badge
                    className={
                      quote.statut === "accepte"
                        ? "bg-green-100 text-green-800"
                        : quote.statut === "refuse"
                        ? "bg-red-100 text-red-800"
                        : quote.statut === "envoye"
                        ? "bg-purple-100 text-purple-800"
                        : "bg-gray-100 text-gray-800"
                    }
                  >
                    {quote.statut === "accepte"
                      ? "Accepté"
                      : quote.statut === "refuse"
                      ? "Refusé"
                      : quote.statut === "envoye"
                      ? "En attente"
                      : quote.statut}
                  </Badge>
                </div>

                {/* Quote Lines */}
                <div className="space-y-2 mb-3">
                  {quote.lignes?.map((ligne, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {ligne.description} x{ligne.quantite}
                      </span>
                      <span>{ligne.total?.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>

                <Separator className="my-3" />

                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg">{quote.total_ttc?.toFixed(2)}€ TTC</span>

                  {quote.statut === "envoye" && (
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleQuoteResponse(quote.id, "refuse")}
                        data-testid={`refuse-quote-${quote.id}`}
                      >
                        Refuser
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleQuoteResponse(quote.id, "accepte")}
                        data-testid={`accept-quote-${quote.id}`}
                      >
                        Accepter
                      </Button>
                    </div>
                  )}
                  <button
                    onClick={() => downloadPdf(`/api/portal/pdf/devis/${quote.id}`, `Devis_${quote.numero}.pdf`)}
                    disabled={downloadingPdf === `/api/portal/pdf/devis/${quote.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                    data-testid={`download-devis-${quote.id}`}
                  >
                    {downloadingPdf === `/api/portal/pdf/devis/${quote.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
                  </button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* History */}
      {caseData.historique && caseData.historique.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Historique
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[...caseData.historique].reverse().map((entry, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                    {idx < caseData.historique.length - 1 && (
                      <div className="w-0.5 flex-1 bg-border mt-1" />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className="text-sm font-medium">
                      {statusLabels[entry.statut] || entry.commentaire || entry.action}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleString("fr-FR")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientPortalSAVDetail;
